import { collection, onSnapshot, query, where } from "firebase/firestore"
import { useCallback, useEffect } from "react"
import { db } from "../firebase/config"
import useTapeTechnicalRequests from "../store/Designer/useTapeTechnicalRequests"
import { useAuthContext } from "./useAuthContext"
import useFetchPriceRequests from "../store/Manager/useFetchPriceRequests"
import useAdminActiveRequests from "../store/Admin/useAdminActiveRequests"
import useFetchTickets from "../store/Tickets/useTicketsFetch"
import useHoldOrders from "../store/Accounting/useHoldOrders"
import useAwaitingDocWarehouse from "../store/SidebarNotifs/useAwaitingDocWarehouse"
import usePrePaymentOrders from "../store/SidebarNotifs/usePrePaymentOrders"
import useFetchTicketsHoldOrders from "../store/Tickets/useFetchTicketHoldOrders"
import { calculateProjectedMeters } from "../components/Orders/Helpers/CalculateProjectedMeters"
import useFetchUpcomingDesignerOrders from "../store/PaperManager/useFetchUpcomingDesignerOrders"

const useFetchUserBasedData = () => {
  const { designer, accountant, user, admin, sales, paperManager } = useAuthContext()
  const { setTechnicalRequests, setLoading } = useTapeTechnicalRequests()
  const { setPriceRequests, setPriceRequestsLoading } = useFetchPriceRequests()
  const { setActiveRequests, setActiveRequestLoading } = useAdminActiveRequests()
  const { setTickets, setTicketsLoading } = useFetchTickets()
  const { setHoldOrders, setHoldOrsersLoading } = useHoldOrders()
  const { setAwaitingDocWarehouseOrders, setAwaitingOrdersLoading } = useAwaitingDocWarehouse()
  const { setPrePaymentOrders, setPrePaymentsLoading } = usePrePaymentOrders()
  const { setTicketsHoldOrders, setTicketsHoldOrdersLoading } = useFetchTicketsHoldOrders()
  const { setDesignerOrders, setDesignerOrdersLoading } = useFetchUpcomingDesignerOrders()

  //FETCH FUNCTIONS
  //designer data
  const fetchDesignerData = (setLoading, setTechnicalRequests) => {
    setLoading(true)
    const ordersCollection = collection(db, "tapePrices")
    const orderQuery = query(ordersCollection, where("hasTechincalPage", "==", 1))

    return onSnapshot(orderQuery, (snapshot) => {
      const doneOrders = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      setTechnicalRequests(doneOrders)
      setLoading(false)
    })
  }

  //sales data
  const fetchAdminRequests = useCallback(
    (setPriceRequestsLoading, setPriceRequests) => {
      setPriceRequestsLoading(true)
      const priceCol = collection(db, "priceCalculation")
      const priceQ = query(
        priceCol,
        where("adminRequested", "==", true),
        where("user", "==", user.uid),
        where("adminRequestStatus", "==", 1)
      )

      return onSnapshot(priceQ, (snapshot) => {
        const priceList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        setPriceRequests(priceList)
        setPriceRequestsLoading(false)
      })
    },
    [user]
  )
  const fetchWarehouseWaitingOrders = useCallback(
    (setAwaitingOrdersLoading, setAwaitingDocWarehouseOrders) => {
      setAwaitingOrdersLoading(true)
      const orderRef = collection(db, "orders")
      const orderQ = query(
        orderRef,
        where("uid", "==", user.uid),
        where("orderStatus", "==", 6),
        where("hasInvoice", "==", false)
      )

      return onSnapshot(orderQ, (snapshot) => {
        const priceList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        setAwaitingDocWarehouseOrders(priceList)
        setAwaitingOrdersLoading(false)
      })
    },
    [user]
  )

  const fetchPrePaymentOrders = useCallback(
    (setPrePaymentsLoading, setPrePaymentOrders) => {
      setPrePaymentsLoading(true)
      const orderRef = collection(db, "orders")
      const orderQ = query(orderRef, where("uid", "==", user.uid), where("orderStatus", "==", 0.5))

      return onSnapshot(orderQ, (snapshot) => {
        const priceList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        setPrePaymentOrders(priceList)
        setPrePaymentsLoading(false)
      })
    },
    [user]
  )

  const fetchDesignerOrders = useCallback((setDesignerOrdersLoading, setDesignerOrders) => {
    setDesignerOrdersLoading(true)
    const orderRef = collection(db, "orders")
    const orderQ = query(orderRef, where("orderStatus", "==", 1))

    return onSnapshot(orderQ, (snapshot) => {
      const priceList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      setDesignerOrders(priceList)
      setDesignerOrdersLoading(false)
    })
  }, [])

  //fetching tickets
  const fetchTickets = useCallback(
    (setTicketsLoading, setTickets) => {
      setTicketsLoading(true)
      const ticketRef = collection(db, "tickets")
      const ticketQ = query(ticketRef, where("assignedToId", "array-contains", user.uid))

      return onSnapshot(ticketQ, (snapshot) => {
        const ticketList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))

        setTickets(ticketList)
        setTicketsLoading(false)
      })
    },
    [user]
  )

  const fetchTicketsHoldOrders = useCallback(
    (setTicketsHoldOrdersLoading, setTicketHoldOrders) => {
      setTicketsHoldOrdersLoading(true)
      const ordersRef = collection(db, "orders")

      if (sales) {
        // --- THIS IS THE CORRECTED PART ---
        // Combine both conditions into a single, efficient query.
        // This tells Firestore to get documents WHERE uid matches AND orderStatus is on hold.
        const salesHoldQuery = query(
          ordersRef,
          where("uid", "==", user.uid),
          where("orderStatus", "in", [997, 998])
        )

        // Now we only need ONE snapshot listener.
        const unsubscribe = onSnapshot(salesHoldQuery, async (snapshot) => {
          const orders = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))

          // The merge and deduplication logic is no longer needed.
          const enrichedOrders = await Promise.all(
            orders.map((order) => calculateProjectedMeters(order))
          )

          setTicketHoldOrders(enrichedOrders)
          setTicketsHoldOrdersLoading(false)
        })

        // Return the single unsubscribe function
        return unsubscribe
      } else {
        // The logic for non-sales users remains unchanged, as it correctly
        // fetches all hold orders and then filters them on the client.
        const holdQ = query(ordersRef, where("orderStatus", "in", [997, 998]))

        const unsubscribe = onSnapshot(holdQ, async (snapshot) => {
          const filtered = snapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .filter((doc) => doc.holdOrderAssignedIds?.includes(user.uid))

          const enrichedOrders = await Promise.all(
            filtered.map((order) => calculateProjectedMeters(order))
          )

          setTicketHoldOrders(enrichedOrders)
          setTicketsHoldOrdersLoading(false)
        })

        return unsubscribe
      }
    },
    [user, sales]
  )

  //fetching hold orders
  const fetchHoldOrders = useCallback((setHoldOrsersLoading, setHoldOrders) => {
    setHoldOrsersLoading(true)
    const holdOrdersRef = collection(db, "orders")

    // Query for orders with orderStatus == 0.5 and hasProforma == true
    const queryA = query(
      holdOrdersRef,
      where("orderStatus", "==", 0.5),
      where("hasProforma", "==", true)
    )

    // Query for orders with orderStatus == 6 and hasInvoice == true
    const queryB = query(
      holdOrdersRef,
      where("orderStatus", "==", 6),
      where("hasInvoice", "==", true)
    )

    // Local arrays to store the results from each query.
    let ordersA = []
    let ordersB = []

    // A helper function to merge results and update state.
    const mergeOrders = () => {
      const merged = [...ordersA, ...ordersB]
      setHoldOrders(merged)
      setHoldOrsersLoading(false)
    }

    // Subscribe to the first query.
    const unsubscribeA = onSnapshot(queryA, (snapshot) => {
      ordersA = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      mergeOrders()
    })

    // Subscribe to the second query.
    const unsubscribeB = onSnapshot(queryB, (snapshot) => {
      ordersB = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      mergeOrders()
    })

    // Return a cleanup function that unsubscribes both listeners.
    return () => {
      unsubscribeA()
      unsubscribeB()
    }
  }, [])

  //admin data
  const fetchActiveAdminRequests = useCallback((setActiveRequestLoading, setActiveRequests) => {
    setActiveRequestLoading(true)
    const ordersCollection = collection(db, "priceCalculation")
    const orderQuery = query(ordersCollection, where("adminRequested", "==", true))

    return onSnapshot(orderQuery, (snapshot) => {
      const doneOrders = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      setActiveRequests(doneOrders)
      setActiveRequestLoading(false)
    })
  }, [])

  //fetches user based data
  useEffect(() => {
    let unsubscribeDesign,
      unsubscribeAccount,
      unsubscribeAdmin,
      unsubscribeAdminActive,
      unsubTickets,
      unsubscribeHoldOrders,
      unsubAwaitingWarehouseOrders,
      unsubPrePaymentOrders,
      unsubTicketOrders,
      unsubDesignerOrders

    if (user) {
      unsubTickets = fetchTickets(setTicketsLoading, setTickets)
      unsubTicketOrders = fetchTicketsHoldOrders(setTicketsHoldOrdersLoading, setTicketsHoldOrders)
      unsubDesignerOrders = fetchDesignerOrders(setDesignerOrdersLoading, setDesignerOrders)

      if (sales) {
        unsubAwaitingWarehouseOrders = fetchWarehouseWaitingOrders(
          setAwaitingOrdersLoading,
          setAwaitingDocWarehouseOrders
        )
        unsubPrePaymentOrders = fetchPrePaymentOrders(setPrePaymentsLoading, setPrePaymentOrders)
      }

      if (designer) {
        unsubscribeDesign = fetchDesignerData(setLoading, setTechnicalRequests)
      }

      if (accountant) {
        // Call fetchHoldOrders and store the unsubscribe function
        unsubscribeHoldOrders = fetchHoldOrders(setHoldOrsersLoading, setHoldOrders)
      }

      if (admin) {
        unsubscribeAdminActive = fetchActiveAdminRequests(
          setActiveRequestLoading,
          setActiveRequests
        )
        // Admin also fetches general admin requests
        unsubscribeAdmin = fetchAdminRequests(setPriceRequestsLoading, setPriceRequests)
      } else {
        unsubscribeAdmin = fetchAdminRequests(setPriceRequestsLoading, setPriceRequests)
      }
    }

    return () => {
      unsubTickets?.()
      unsubscribeDesign?.()
      unsubscribeAccount?.()
      unsubscribeHoldOrders?.() // Clean up the hold orders subscription
      unsubscribeAdminActive?.()
      unsubscribeAdmin?.()
      unsubAwaitingWarehouseOrders?.()
      unsubPrePaymentOrders?.()
      unsubTicketOrders?.()
      unsubDesignerOrders?.()
    }
  }, [
    designer,
    accountant,
    admin,
    user,
    setLoading,
    setTechnicalRequests,
    setActiveRequestLoading,
    setActiveRequests,
    setPriceRequestsLoading,
    setPriceRequests,
    fetchAdminRequests,
    fetchActiveAdminRequests,
    fetchTickets,
    setTickets,
    setTicketsLoading,
    // Include dependencies related to fetchHoldOrders
    setHoldOrsersLoading,
    setHoldOrders,
    fetchHoldOrders,
    sales,
    fetchWarehouseWaitingOrders,
    setAwaitingDocWarehouseOrders,
    setAwaitingOrdersLoading,
    fetchPrePaymentOrders,
    setPrePaymentsLoading,
    setPrePaymentOrders,
    paperManager,
    setTicketsHoldOrders,
    setTicketsHoldOrdersLoading,
    fetchTicketsHoldOrders,
    fetchDesignerOrders,
    setDesignerOrders,
    setDesignerOrdersLoading,
  ])
}

export default useFetchUserBasedData
